import React, { useState, useEffect } from 'react';
import { keyframes } from '@mui/system';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import hpeLogo from '../assets/images/hpe-logo.png';
import veeamLogo from '../assets/images/veeam-logo.png';
import microsoftLogo from '../assets/images/microsoft logo.png';
import nutanixLogo from '../assets/images/nutanix-logo.png';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideAnimation = keyframes`
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const PartnershipSlideshow: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('partners');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const partners = [
    { name: 'HPE', logo: hpeLogo },
    { name: 'Microsoft', logo: microsoftLogo },
    { name: 'Nutanix', logo: nutanixLogo },
    { name: 'Veeam', logo: veeamLogo },
  ];

  return (
    <Box
      id="partners"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#f8f9fa',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              animation: isVisible ? `${fadeIn} 0.6s ease-out` : 'none',
            }}
          >
            Our Technology Partners
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: 'auto',
              animation: isVisible ? `${fadeIn} 0.6s ease-out 0.2s both` : 'none',
            }}
          >
            We partner with industry-leading technology providers to deliver best-in-class solutions
          </Typography>
        </Box>

        {/* Logo Slideshow */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            py: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              animation: `${slideAnimation} 20s linear infinite`,
            }}
          >
            {/* Render logos twice for seamless loop */}
            {[...partners, ...partners].map((partner, index) => (
              <Box
                key={index}
                sx={{
                  flex: '0 0 25%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 4,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  loading="lazy"
                  style={{
                    maxWidth: '150px',
                    maxHeight: '80px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    transition: 'all 0.3s ease',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              animation: isVisible ? `${fadeIn} 0.6s ease-out 1s both` : 'none',
            }}
          >
            Certified partners delivering enterprise-grade solutions to businesses worldwide
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PartnershipSlideshow;
